import React from 'react';
import { Link } from 'react-router-dom';
import '../newsite/newsite.css';
import {
  HiggsfieldHero,
  SiteEffects,
  SiteHeader,
  SiteFooter,
  GrowthOSVideo,
  useReveal,
  usePageMeta,
  GROWTHOS_SIGNUP_URL,
} from '../newsite/SiteChrome';

export default function GrowthOSCaseStudy() {
  useReveal();
  usePageMeta(
    'GrowthOS Case Study — X Scale',
    'How X Scale helped create GrowthOS by Smart Marketer: agentic infrastructure for ecommerce brands.'
  );

  return (
    <div className="xscale-site case-page">
      <SiteEffects />
      <SiteHeader onCasePage />

      <main>
        {/* CASE HERO */}
        <section className="case-hero section-shell" data-testid="case-hero">
          <HiggsfieldHero />
          <div className="hero-grid" aria-hidden="true" />
          <div className="case-hero-copy">
            <Link className="back-link" to="/#projects" data-testid="back-to-projects">← Back to Projects</Link>
            <div className="eyebrow"><span /> CASE STUDY / GROWTHOS BY SMART MARKETER</div>
            <h1>Turning ecommerce expertise into agentic execution.</h1>
            <p className="hero-copy">
              GrowthOS is an ecommerce operating system where brands can ask Gary in Slack to build
              landing pages, generate creative, draft ad campaigns, ship email flows, and report
              back with the numbers.
            </p>
            <div className="hero-actions case-actions">
              <a className="button button-primary" href={GROWTHOS_SIGNUP_URL} target="_blank" rel="noreferrer" data-testid="case-signup-btn">Apply for GrowthOS</a>
              <a className="button button-secondary" href="#takeaway" data-testid="case-why-btn">See Why It Matters</a>
            </div>
          </div>
          <div className="case-hero-demo growthos-visual">
            <GrowthOSVideo caption="Gary in Slack: brief in, landing page + creative + ads out." />
          </div>
        </section>

        {/* SUMMARY STATS */}
        <section className="case-summary section-shell reveal" data-testid="case-summary">
          <div className="case-stat"><span>PROJECT TYPE</span><b>Agentic operating system</b></div>
          <div className="case-stat"><span>MARKET</span><b>Ecommerce brands</b></div>
          <div className="case-stat"><span>MODEL</span><b>Partner project</b></div>
          <div className="case-stat"><span>PROOF</span><b>Public flagship</b></div>
        </section>

        {/* THE PROBLEM */}
        <section className="case-section section-shell reveal" data-testid="case-problem">
          <div className="section-label">THE PROBLEM</div>
          <div className="case-two-col">
            <h2>Ecommerce teams have the playbooks. They don&rsquo;t have infinite execution.</h2>
            <div className="thesis-copy">
              <p>Growth teams know what needs to happen: landing pages, new angles, creative iterations, ad drafts, email flows, performance reporting, customer insight loops.</p>
              <p>The bottleneck is that the work is spread across people, documents, platforms, meetings, and manual follow-up. Even strong operators lose speed when every launch requires another round of coordination.</p>
            </div>
          </div>
        </section>

        {/* THE BUILD */}
        <section className="case-section section-shell reveal" data-testid="case-build">
          <div className="section-label">THE BUILD</div>
          <div className="builds-grid case-build-grid">
            <article>
              <span>01 / GARY</span>
              <h3>The coordinating agent.</h3>
              <p>Gary acts as the ecommerce strategist in Slack: routing work, orchestrating specialized agents, and keeping the team in the loop.</p>
            </article>
            <article>
              <span>02 / SPECIALISTS</span>
              <h3>Agents for the actual work.</h3>
              <p>Landing pages, creative, ads, email, analytics, brand voice, and operations become execution lanes — not abstract prompts.</p>
            </article>
            <article>
              <span>03 / STACK CONNECTIONS</span>
              <h3>Built around existing systems.</h3>
              <p>GrowthOS is designed for the stack ecommerce teams already use: Shopify, Klaviyo, Meta, Google, Workspace, analytics, and search tools.</p>
            </article>
          </div>
        </section>

        {/* WHY IT MATTERS */}
        <section className="case-section section-shell reveal" data-testid="case-why">
          <div className="section-label">WHY IT MATTERS</div>
          <div className="model-panel">
            <h2>This is the X Scale model in public.</h2>
            <p>
              GrowthOS shows what X Scale means by agentic infrastructure: not a chatbot, not an
              automation, not another dashboard. It is a repeatable operating layer built on a
              partner&rsquo;s expertise, market, workflows, and distribution.
            </p>
            <div className="criteria-grid">
              <span>Expertise becomes system logic</span>
              <span>Workflows become agent runs</span>
              <span>Slack becomes the interface</span>
              <span>Operators stay in control</span>
              <span>Execution gets repeatable</span>
              <span>The project becomes an asset</span>
            </div>
          </div>
        </section>

        {/* STRATEGIC TAKEAWAY */}
        <section id="takeaway" className="case-section section-shell reveal" data-testid="case-takeaway">
          <div className="section-label">STRATEGIC TAKEAWAY</div>
          <div className="case-two-col">
            <h2>Traditional companies with leverage can become infrastructure-backed companies.</h2>
            <div className="thesis-copy">
              <p>Smart Marketer already had audience, credibility, ecommerce knowledge, and operator-level playbooks. GrowthOS turns that leverage into software-like infrastructure that can execute for brands.</p>
              <p>This is the kind of project X Scale exists to build: a traditional business asset amplified by agents, systems, and repeatable execution.</p>
              <p>
                <a className="button button-primary" href={GROWTHOS_SIGNUP_URL} target="_blank" rel="noreferrer" data-testid="takeaway-signup-btn">Apply for GrowthOS</a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter onCasePage />
    </div>
  );
}
